import React from 'react';

// Corner Brackets for Technical Panels
export const CornerBrackets = ({ className = "" }) => (
  <>
    <div className={`absolute top-0 left-0 w-3 h-3 border-t border-l border-emerald-500/30 ${className}`} />
    <div className={`absolute top-0 right-0 w-3 h-3 border-t border-r border-emerald-500/30 ${className}`} />
    <div className={`absolute bottom-0 left-0 w-3 h-3 border-b border-l border-emerald-500/30 ${className}`} />
    <div className={`absolute bottom-0 right-0 w-3 h-3 border-b border-r border-emerald-500/30 ${className}`} />
  </>
);

// Technical Badge Component
export const TechBadge = ({ children, color = "emerald", className = "" }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    yellow: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/10 border-red-500/20 text-red-400'
  };

  return (
    <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-sm border ${colorClasses[color]} text-[10px] font-mono tracking-widest uppercase ${className}`}>
      {children}
    </div>
  );
};

// Technical Card with Scanline Effect
export const TechCard = ({ children, className = "", hover = true, onClick, ...props }) => (
  <div 
    className={`relative bg-[#0B0F19] border border-white/5 p-6 group ${hover ? 'hover:border-emerald-500/30' : ''} transition-colors duration-300 ${className}`}
    onClick={onClick}
    {...props}
  >
    <CornerBrackets />
    {/* Scanline Effect */}
    {hover && (
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-500 bg-[length:100%_4px]" />
    )}
    <div className="relative z-10">{children}</div>
  </div>
);

// Grid Background Component
export const GridBackground = ({ children, className = '' }) => (
  <div className={`relative bg-[#0B0F19] ${className}`}>
    {/* Background Layer */}
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
    {/* Base Grid */}
    <div 
      className="absolute inset-0" 
      style={{
        backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
                          linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }}
    />
    
    {/* Glowing Intersection */}
    <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.03), transparent 70%)' }} />
    </div>
    
    {/* Content Layer */}
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

// Status Indicator
export const StatusIndicator = ({ active = true, label }) => (
  <div className="flex items-center space-x-2">
    <div className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500 animate-pulse' : 'bg-slate-700'}`} />
    {label && <span className="text-xs font-mono text-slate-500 uppercase">{label}</span>}
  </div>
);

// Technical Button
export const TechButton = ({ 
  children, 
  variant = 'primary', 
  className = '', 
  ...props 
}) => {
  const variants = {
    primary: 'bg-emerald-500 hover:bg-emerald-400 text-black border-emerald-500',
    secondary: 'bg-transparent hover:bg-white/5 text-white border-white/20 hover:border-white/40',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30'
  };

  return (
    <button 
      className={`px-6 py-3 font-mono text-xs font-bold border transition-all ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Technical Input Field
export const TechInput = ({ 
  label, 
  icon: Icon, 
  className = '', 
  error,
  ...props 
}) => (
  <div className="space-y-2">
    {label && (
      <label className="block text-xs font-mono text-slate-400 uppercase tracking-wider">
        {label}
      </label>
    )}
    <div className="relative">
      {Icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
          <Icon className="w-4 h-4" />
        </div>
      )}
      <input
        className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 bg-[#0B0F19] border ${error ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition-colors font-mono text-sm ${className}`}
        {...props}
      />
      {error && (
        <p className="text-xs text-red-400 font-mono mt-1">{error}</p>
      )}
    </div>
  </div>
);

// Data Panel with Header
export const DataPanel = ({ title, badge, children, className = "" }) => (
  <TechCard className={className}>
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-sm font-mono font-bold text-white uppercase tracking-wider">{title}</h3>
      {badge && <TechBadge>{badge}</TechBadge>}
    </div>
    {children}
  </TechCard>
);

export default {
  CornerBrackets,
  TechBadge,
  TechCard,
  GridBackground,
  StatusIndicator,
  TechButton,
  TechInput,
  DataPanel
};
